/**
 * window.JessieColour — the one Jessie Premium colour lookup.
 *
 * Liquid cannot read a JavaScript global, so every Jessie surface renders an
 * empty labelled shell and a deferred script fills it. This file is what the
 * fillers call. It is the ONLY implementation of the join, the family
 * classification, the twins and the colour maths: a second copy in a section
 * would drift the moment one of them was fixed.
 *
 * DATA (all optional except the first; each is a plain deferred <script src>)
 *   window.MCM_COLOURS          assets/material-colours.js      - required
 *   window.JESSIE_COLOUR_EXTRAS assets/jessie-colour-extras.js  - FHI/CMYK/Lab D50
 *   window.JESSIE_PRINT_PROFILES assets/jessie-print-profiles.js - PrusaSlicer rows
 *   window.JESSIE_LINE_LABELS   snippets/jessie-line-labels.liquid - display names
 *
 * With MCM_COLOURS absent every getter returns null or an empty array and
 * ready() never runs its callback, so a blocked or 404'd asset ends where a
 * missing colour ends: nothing renders.
 *
 * SCOPE. line(), byLineAndName(), twins() and familyCounts() only ever see
 * rows with brand "Jessie" — the table also holds 88 Prusament rows and this
 * build is about Jessie. byId()/byHandle() are exact-identity lookups and
 * search the whole table, but resolve() — what consumers actually call — will
 * not return a non-Jessie row or a row from another line.
 *
 * Every getter returns a CLONE. The live Colour Match tool writes `__de` onto
 * MCM_COLOURS entries and back-fills `lab`; handing two consumers the same
 * object invites exactly that kind of action at a distance.
 *
 * This file is also the tested one: shared/jessie-colour.js evaluates these
 * bytes with a stand-in window and re-exports them, so test/jessie-colour.test.js
 * exercises what ships rather than a copy of it.
 */
(function () {
  'use strict';

  // A section may render twice on one page, so this src can be emitted twice.
  // The data assets are plain assignments and are harmless to repeat; this one
  // holds ready() callbacks, so it defines itself once.
  if (window.JessieColour) return;

  var BRAND = 'Jessie';

  /** Display order of the palette chips. Ported from build.py FAMILY_ORDER. */
  var FAMILIES = [
    'Reds & oranges',
    'Yellows & greens',
    'Blues & purples',
    'Neutrals',
    'Neon',
    'Glitter',
  ];

  /** The three Jessie lines, in the order the pages present them. */
  var LINES = ['PLA', 'PETG', 'Elixir'];

  /** Defaults for lineLabel() when neither the theme settings nor MCM answer. */
  var LINE_LABEL_FALLBACK = {
    PLA: 'Jessie Premium PLA',
    PETG: 'Jessie Premium PETG',
    Elixir: 'Jessie Premium Elixir™',
  };

  /* ------------------------------------------------------------------ table */

  function table() {
    return Array.isArray(window.MCM_COLOURS) ? window.MCM_COLOURS : null;
  }

  /** Rows are plain JSON, so a JSON round-trip is a complete and cheap clone. */
  function clone(value) {
    if (value === null || value === undefined) return value;
    return JSON.parse(JSON.stringify(value));
  }

  /** The join key everywhere: trim, and collapse internal runs of whitespace. */
  function cleanName(value) {
    return String(value === null || value === undefined ? '' : value).replace(/\s+/g, ' ').trim();
  }

  function nameKey(value) {
    return cleanName(value).toLowerCase();
  }

  function jessieRows() {
    var rows = table();
    if (!rows) return [];
    return rows.filter(function (row) {
      return row && row.brand === BRAND;
    });
  }

  /**
   * Runs `fn` once the colour table is present, and never runs it at all if it
   * is not. Deferred scripts run in document order, so a consumer loaded after
   * material-colours.js already has the table; the DOMContentLoaded branch is
   * for the inline script that runs earlier than either.
   */
  function ready(fn) {
    if (typeof fn !== 'function') return;
    if (table()) {
      fn();
      return;
    }
    if (typeof document !== 'undefined' && document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        if (table()) fn();
      });
    }
  }

  /* ---------------------------------------------------------------- lookups */

  /** `/products/x?y#z` and `x` both reduce to `x`. */
  function handleOf(value) {
    var raw = String(value === null || value === undefined ? '' : value).trim();
    if (!raw) return '';
    return raw
      .split('?')[0]
      .split('#')[0]
      .replace(/^https?:\/\/[^/]+/i, '')
      .replace(/^\/+/, '')
      .replace(/^products\//, '')
      .replace(/\/+$/, '');
  }

  function byHandle(handle) {
    var wanted = handleOf(handle);
    if (!wanted) return null;
    var rows = table();
    if (!rows) return null;
    for (var i = 0; i < rows.length; i++) {
      if (rows[i] && handleOf(rows[i].url) === wanted) return clone(rows[i]);
    }
    return null;
  }

  function byId(id) {
    var wanted = String(id === null || id === undefined ? '' : id).trim();
    if (!wanted) return null;
    var rows = table();
    if (!rows) return null;
    for (var i = 0; i < rows.length; i++) {
      if (rows[i] && rows[i].id === wanted) return clone(rows[i]);
    }
    return null;
  }

  /**
   * Jessie rows for one line.
   *
   * Filtered on `line`, NEVER on `material`: every Elixir row carries
   * material "PLA" (it is a PLA/TPU blend), so a material filter would fold
   * the nine Elixir colours silently into the PLA palette.
   */
  function line(name) {
    var wanted = String(name === null || name === undefined ? '' : name).trim();
    if (!wanted) return [];
    return jessieRows()
      .filter(function (row) {
        return row.line === wanted;
      })
      .map(clone);
  }

  function byLineAndName(lineName, colourName) {
    var key = nameKey(colourName);
    if (!key) return null;
    var rows = line(lineName);
    for (var i = 0; i < rows.length; i++) {
      if (nameKey(rows[i].name) === key) return rows[i];
    }
    return null;
  }

  /**
   * The two-step join every consumer calls, so nobody implements step two twice.
   *
   * MCM holds one row per colour — all 1 kg, all 1.75 mm — while the store
   * holds 74 PLA and 25 PETG products for 36 and 12 colours, because every
   * other weight and diameter is its own product with its own handle. So the
   * handle hits on the 1 kg PDP and misses on the 500 g one, and a miss there
   * is indistinguishable from "not a Jessie product" unless line + name
   * carries it.
   *
   * A handle hit from another brand or another line is discarded rather than
   * returned: the block asking is a Jessie block on a known line.
   */
  function resolve(query) {
    var q = query || {};
    var wantedLine = String(q.line === null || q.line === undefined ? '' : q.line).trim();
    var hit = byHandle(q.handle);
    if (hit && hit.brand === BRAND && (!wantedLine || hit.line === wantedLine)) return hit;
    return byLineAndName(wantedLine, q.name);
  }

  /* ----------------------------------------------------------------- extras */

  function extras(id) {
    var extrasTable = window.JESSIE_COLOUR_EXTRAS;
    if (!extrasTable || typeof extrasTable !== 'object') return {};
    var key = String(id === null || id === undefined ? '' : id);
    if (!Object.prototype.hasOwnProperty.call(extrasTable, key)) return {};
    return clone(extrasTable[key]) || {};
  }

  /**
   * A row plus the four fields the generated colour table does not carry.
   *
   * `lab_d50` is the MEASURED Nix D50/2° reading and is not `row.lab`, which
   * the Colour Match section computes from the hex for its dE maths. Anything
   * printing a row labelled "CIELAB, D50/2°" wants this one.
   */
  function withExtras(row) {
    if (!row) return row;
    var merged = clone(row);
    var extra = extras(row.id);
    for (var key in extra) {
      if (Object.prototype.hasOwnProperty.call(extra, key)) merged[key] = extra[key];
    }
    return merged;
  }

  /* --------------------------------------------------------------- families */

  function hexToRgb(hex) {
    var h = String(hex === null || hex === undefined ? '' : hex).replace('#', '').trim();
    if (h.length === 3) {
      h = h
        .split('')
        .map(function (c) {
          return c + c;
        })
        .join('');
    }
    if (!/^[0-9a-f]{6}$/i.test(h)) return null;
    var n = parseInt(h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }

  function hexToHsl(hex) {
    var rgb = hexToRgb(hex);
    if (!rgb) return null;
    var r = rgb[0] / 255;
    var g = rgb[1] / 255;
    var b = rgb[2] / 255;
    var mx = Math.max(r, g, b);
    var mn = Math.min(r, g, b);
    var l = (mx + mn) / 2;
    if (mx === mn) return { h: 0, s: 0, l: l };
    var d = mx - mn;
    var s = d / (1 - Math.abs(2 * l - 1));
    var h;
    if (mx === r) h = ((g - b) / d + 6) % 6;
    else if (mx === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    return { h: h * 60, s: s, l: l };
  }

  /**
   * Which palette chip a colour sits under. Ported verbatim from build.py's
   * `family()`, including the order of the tests: "Neon Green" is Neon, not
   * Yellows & greens, and "Golden Winner Glitter" is Glitter, not Yellows.
   */
  function family(row) {
    if (!row) return null;
    var name = cleanName(row.name).toLowerCase();
    if (name.indexOf('glitter') > -1) return 'Glitter';
    if (name.indexOf('neon') > -1) return 'Neon';
    var hsl = hexToHsl(row.hex);
    if (!hsl) return null;
    if (hsl.s < 0.13 || hsl.l > 0.93 || hsl.l < 0.12) return 'Neutrals';
    if (hsl.h < 45 || hsl.h >= 330) return 'Reds & oranges';
    if (hsl.h < 160) return 'Yellows & greens';
    return 'Blues & purples';
  }

  /** Counts keyed by family, in FAMILIES order, families with none omitted. */
  function familyCounts(rows) {
    var tally = {};
    (rows || []).forEach(function (row) {
      var fam = family(row);
      if (!fam) return;
      tally[fam] = (tally[fam] || 0) + 1;
    });
    var ordered = {};
    FAMILIES.forEach(function (fam) {
      if (tally[fam]) ordered[fam] = tally[fam];
    });
    return ordered;
  }

  /**
   * The order a row of swatches reads best in: a gradient, not a shuffle.
   *
   * The table's own order is alphabetical by name, which on a strip of
   * thirty-six dots looks random (owner feedback, 2026-09-06). A plain hue
   * sort was the first fix, and it still jumped: a dark brown between two
   * bright oranges, an olive between two neons. So the chromatic colours are
   * ordered as a path through Lab space — each colour next to the one it is
   * closest to by ΔE2000 — which is what a gradient is. The path starts at the
   * pink/red end of the hue wheel, is built nearest-neighbour first and then
   * straightened with 2-opt until no reversal of a run shortens it. Thirty
   * colours is a few hundred ΔE evaluations; it is not worth caching.
   *
   * Neutrals — low chroma (RGB max minus min under 0.06: Deep Purple's 0.075
   * keeps it a purple, Elixir Natural's 0.047 makes it a neutral), or very
   * light or very dark — come after the gradient, white to black. Rows with
   * no usable hex come last in input order. Returns a new array.
   */
  function displayOrder(rows) {
    var chromatic = [];
    var neutral = [];
    var rest = [];
    (rows || []).forEach(function (row) {
      var rgb = row ? hexToRgb(row.hex) : null;
      if (!rgb) {
        rest.push(row);
        return;
      }
      var hsl = hexToHsl(row.hex);
      var chroma = (Math.max(rgb[0], rgb[1], rgb[2]) - Math.min(rgb[0], rgb[1], rgb[2])) / 255;
      var entry = { row: row, hue: (hsl.h + 30) % 360, light: hsl.l, lab: rgbToLab(rgb) };
      if (chroma < 0.06 || hsl.l > 0.93 || hsl.l < 0.12) neutral.push(entry);
      else chromatic.push(entry);
    });

    neutral.sort(function (a, b) {
      return b.light - a.light;
    });

    return gradientPath(chromatic)
      .concat(neutral)
      .map(function (entry) {
        return entry.row;
      })
      .concat(rest);
  }

  /** Shortest-looking open path through `entries` (each carrying `lab` and `hue`). */
  function gradientPath(entries) {
    var n = entries.length;
    if (n < 3) return entries.slice();

    var dist = [];
    for (var i = 0; i < n; i++) {
      dist[i] = [];
      for (var j = 0; j < n; j++) {
        dist[i][j] = i === j ? 0 : deltaE2000(entries[i].lab, entries[j].lab);
      }
    }

    /* Start at the reddest end of the wheel: the smallest hue once the wheel
       is rotated so that pinks and reds sit at 0. */
    var start = 0;
    for (var s = 1; s < n; s++) {
      if (entries[s].hue < entries[start].hue) start = s;
    }

    /* Nearest neighbour first… */
    var path = [start];
    var used = {};
    used[start] = true;
    while (path.length < n) {
      var last = path[path.length - 1];
      var next = -1;
      for (var k = 0; k < n; k++) {
        if (used[k]) continue;
        if (next === -1 || dist[last][k] < dist[last][next]) next = k;
      }
      used[next] = true;
      path.push(next);
    }

    /* …then 2-opt: reverse any run whose reversal shortens the path, until
       none does. The start stays fixed. Bounded, so a pathological table
       cannot spin. */
    var improved = true;
    var passes = 0;
    while (improved && passes < 100) {
      improved = false;
      passes += 1;
      for (var a = 1; a < n - 1; a++) {
        for (var b = a + 1; b < n; b++) {
          var before = dist[path[a - 1]][path[a]] + (b + 1 < n ? dist[path[b]][path[b + 1]] : 0);
          var after = dist[path[a - 1]][path[b]] + (b + 1 < n ? dist[path[a]][path[b + 1]] : 0);
          if (after < before - 1e-9) {
            var run = path.slice(a, b + 1).reverse();
            path = path.slice(0, a).concat(run, path.slice(b + 1));
            improved = true;
          }
        }
      }
    }

    return path.map(function (index) {
      return entries[index];
    });
  }

  /** The same colour in the other Jessie lines: what "Also available in" shows. */
  function twins(row) {
    if (!row) return [];
    var key = nameKey(row.name);
    if (!key) return [];
    return jessieRows()
      .filter(function (other) {
        return other.line !== row.line && nameKey(other.name) === key;
      })
      .map(clone);
  }

  /* ------------------------------------------------------------ colour maths
     Lifted from sections/material-colour-match.liquid so a ΔE computed on a
     featured page and a ΔE computed by the live tool are the same number. */

  function rgbToLab(rgb) {
    function lin(c) {
      c /= 255;
      return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    }
    var r = lin(rgb[0]);
    var g = lin(rgb[1]);
    var b = lin(rgb[2]);
    var x = (0.4124564 * r + 0.3575761 * g + 0.1804375 * b) / 0.95047;
    var y = 0.2126729 * r + 0.7151522 * g + 0.072175 * b;
    var z = (0.0193339 * r + 0.119192 * g + 0.9503041 * b) / 1.08883;
    function f(t) {
      return t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116;
    }
    var fx = f(x);
    var fy = f(y);
    var fz = f(z);
    return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
  }

  function deltaE2000(labA, labB) {
    if (!labA || !labB) return null;
    var l1 = labA[0];
    var a1 = labA[1];
    var b1 = labA[2];
    var l2 = labB[0];
    var a2 = labB[1];
    var b2 = labB[2];
    var d2r = Math.PI / 180;
    var r2d = 180 / Math.PI;
    var c1 = Math.hypot(a1, b1);
    var c2 = Math.hypot(a2, b2);
    var cb = (c1 + c2) / 2;
    var g = 0.5 * (1 - Math.sqrt(Math.pow(cb, 7) / (Math.pow(cb, 7) + Math.pow(25, 7))));
    var a1p = a1 * (1 + g);
    var a2p = a2 * (1 + g);
    var c1p = Math.hypot(a1p, b1);
    var c2p = Math.hypot(a2p, b2);
    function hp(a, b) {
      if (a === 0 && b === 0) return 0;
      var h = Math.atan2(b, a) * r2d;
      return h < 0 ? h + 360 : h;
    }
    var h1p = hp(a1p, b1);
    var h2p = hp(a2p, b2);
    var dL = l2 - l1;
    var dC = c2p - c1p;
    var dh;
    if (c1p * c2p === 0) {
      dh = 0;
    } else {
      dh = h2p - h1p;
      if (dh > 180) dh -= 360;
      else if (dh < -180) dh += 360;
    }
    var dH = 2 * Math.sqrt(c1p * c2p) * Math.sin((dh / 2) * d2r);
    var Lb = (l1 + l2) / 2;
    var Cb = (c1p + c2p) / 2;
    var Hb;
    if (c1p * c2p === 0) {
      Hb = h1p + h2p;
    } else {
      Hb = (h1p + h2p) / 2;
      if (Math.abs(h1p - h2p) > 180) Hb += Hb < 180 ? 180 : -180;
    }
    var T =
      1 -
      0.17 * Math.cos((Hb - 30) * d2r) +
      0.24 * Math.cos(2 * Hb * d2r) +
      0.32 * Math.cos((3 * Hb + 6) * d2r) -
      0.2 * Math.cos((4 * Hb - 63) * d2r);
    var Sl = 1 + (0.015 * Math.pow(Lb - 50, 2)) / Math.sqrt(20 + Math.pow(Lb - 50, 2));
    var Sc = 1 + 0.045 * Cb;
    var Sh = 1 + 0.015 * Cb * T;
    var Rt =
      -2 *
      Math.sqrt(Math.pow(Cb, 7) / (Math.pow(Cb, 7) + Math.pow(25, 7))) *
      Math.sin(60 * Math.exp(-Math.pow((Hb - 275) / 25, 2)) * d2r);
    return Math.sqrt(
      Math.pow(dL / Sl, 2) + Math.pow(dC / Sc, 2) + Math.pow(dH / Sh, 2) + Rt * (dC / Sc) * (dH / Sh)
    );
  }

  /** Perceived lightness, for deciding ink over a swatch. Same test as the tool. */
  function isLight(hex) {
    var rgb = hexToRgb(hex);
    if (!rgb) return false;
    return 0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2] > 186;
  }

  /* ------------------------------------------------------------------ tiles */

  /** The finish overlay class for snippets/jessie-swatch-tile.liquid. */
  function finishClass(row) {
    if (!row) return '';
    if (row.finish === 'silk') return 'jessie-tile--silk';
    if (row.finish === 'glitter') return 'jessie-tile--glitter';
    if (row.finish === 'translucent') return 'jessie-tile--translucent';
    return '';
  }

  /**
   * Paint one tile shell. Not part of the plan's API table, but every consumer
   * does exactly this, so it is here rather than three times over: set the
   * colour custom property, add the finish class, drop the `hidden` the shell
   * ships with. Returns false when there is nothing to paint, which leaves the
   * shell hidden.
   */
  function paintTile(el, row) {
    if (!el || !row || !row.hex) return false;
    el.style.setProperty('--jessie-tile-color', row.hex);
    var cls = finishClass(row);
    if (cls) el.classList.add(cls);
    el.hidden = false;
    return true;
  }

  /* ------------------------------------------------------------ line labels */

  /**
   * The display name for a line. Nine surfaces print one and none of them owns
   * the string: the theme settings do, through
   * snippets/jessie-line-labels.liquid. MCM's own `line_label` is the fallback
   * (it has no trademark symbol), and the line's key is the last resort.
   */
  function lineLabel(name) {
    var wanted = String(name === null || name === undefined ? '' : name).trim();
    if (!wanted) return '';
    var labels = window.JESSIE_LINE_LABELS;
    if (labels && typeof labels === 'object' && labels[wanted]) return String(labels[wanted]);
    var rows = line(wanted);
    if (rows.length && rows[0].line_label) return String(rows[0].line_label);
    return LINE_LABEL_FALLBACK[wanted] || wanted;
  }

  /* ------------------------------------------------------------------- fill */

  var FILL_SEPARATOR = '[·|,;:–—-]';
  var FILL_TOKEN = new RegExp('(\\s*' + FILL_SEPARATOR + '\\s*)?\\{(line_label|line|price|n)\\}', 'g');
  var FILL_LEADING = new RegExp('^\\s*' + FILL_SEPARATOR + '\\s*');

  /**
   * Substitute the closed token set {line}, {line_label}, {n}, {price} in a
   * setting's value. Liquid cannot know {n} — the number of colours in a line
   * is a fact about the colour table — so the consuming script applies this to
   * anything carrying data-jessie-fill.
   *
   * A token with no value takes the separator in front of it with it (or, if
   * it started the string, the one behind it), and the leftover whitespace is
   * collapsed: "Jessie Premium Filament · {line}" degrades to "Jessie Premium
   * Filament" and "{n} colours · in stock" to "colours · in stock", never to a
   * dangling middot.
   * Tokens outside the set are left exactly as they are: a merchant who types
   * {foo} sees {foo} and knows it did nothing.
   */
  function fill(template, vars) {
    var source = String(template === null || template === undefined ? '' : template);
    var values = vars || {};
    var dropped = false;
    var filled = source.replace(FILL_TOKEN, function (match, before, key) {
      var value = values[key];
      if (value === undefined || value === null || value === '') {
        dropped = true;
        return '';
      }
      return (before || '') + String(value);
    });
    /* A token that led the string had no separator of its own to take, so the
       one now stranded at the front goes with it: "{n} · in stock" is
       "in stock", not "· in stock". */
    if (dropped) filled = filled.replace(FILL_LEADING, '');
    return filled.replace(/[ \t]{2,}/g, ' ').trim();
  }

  /* --------------------------------------------------------------- profiles */

  /**
   * The PrusaSlicer rows for a line, following `alias_of` exactly one hop:
   * Elixir prints on the Jessie PLA profiles, and storing that as an alias
   * rather than a copied array keeps it a fact rather than a duplication. The
   * alias entry keeps its OWN note — Elixir's says to raise the temperature
   * for more shine, which is not true of PLA.
   *
   * An unknown line returns null and the caller renders nothing.
   */
  function profilesForLine(name) {
    var data = window.JESSIE_PRINT_PROFILES;
    var wanted = String(name === null || name === undefined ? '' : name).trim();
    if (!data || !data.lines || !wanted) return null;
    var entry = data.lines[wanted];
    if (!entry) return null;
    var note = entry.note;
    if (entry.alias_of) {
      var target = data.lines[entry.alias_of];
      if (!target || !target.rows) return null;
      return { rows: clone(target.rows), note: note || target.note, source: data.source };
    }
    if (!entry.rows) return null;
    return { rows: clone(entry.rows), note: note, source: data.source };
  }

  window.JessieColour = {
    FAMILIES: FAMILIES.slice(),
    LINES: LINES.slice(),
    ready: ready,
    line: line,
    byHandle: byHandle,
    byLineAndName: byLineAndName,
    resolve: resolve,
    byId: byId,
    lineLabel: lineLabel,
    fill: fill,
    extras: extras,
    withExtras: withExtras,
    cleanName: cleanName,
    family: family,
    familyCounts: familyCounts,
    displayOrder: displayOrder,
    twins: twins,
    deltaE2000: deltaE2000,
    hexToRgb: hexToRgb,
    rgbToLab: rgbToLab,
    isLight: isLight,
    finishClass: finishClass,
    paintTile: paintTile,
    profiles: { forLine: profilesForLine },
  };
})();
